import { useCallback, useEffect, useState } from 'react';
import { BloqueAgenda, EspacioAgenda } from './useAgenda';

/**
 * Elegir un hueco arrastrando el ratón sobre la rejilla del día.
 *
 * Como en un calendario de toda la vida: se pulsa en una franja libre, se
 * arrastra, y al soltar queda elegido el rango. Cada franja son 30 minutos.
 *
 * Dos reglas que no se negocian:
 *  - No se puede empezar sobre algo ocupado.
 *  - Al arrastrar, la selección se PARA antes de la primera franja ocupada,
 *    en vez de saltársela. Saltarla dejaría elegir un rango que pisa una
 *    reserva existente, que es justo lo que no puede pasar.
 */

export type Seleccion = {
  espacioId: string;
  /** Fila donde se pulsó; puede ser mayor que `hasta` mientras se arrastra. */
  ancla: number;
  desde: number;
  hasta: number;
};

export type HuecoElegido = {
  espacio: EspacioAgenda;
  filaInicio: number;
  /** Última franja incluida. */
  filaFin: number;
};

/** Filas que ya tienen algo, por sala. Las canceladas no estorban. */
export const filasOcupadas = (bloques: BloqueAgenda[] | undefined): Set<number> => {
  const tomadas = new Set<number>();
  (bloques ?? [])
    .filter((b) => b.estado !== 'cancelled')
    .forEach((b) => {
      for (let f = b.filaInicio; f < b.filaInicio + b.filas; f++) tomadas.add(f);
    });
  return tomadas;
};

export const useSeleccionFranjas = ({
  activo,
  totalFilas,
  ocupadasPorEspacio,
  filasFijasPorEspacio,
  onElegido,
}: {
  /** En las vistas de semana y mes no se selecciona nada. */
  activo: boolean;
  totalFilas: number;
  ocupadasPorEspacio: (espacioId: string) => Set<number>;
  /**
   * Espacios de duración fija (las visitas: sin hora de fin, con bloque):
   * cuántas filas ocupa una reserva. Ahí no se arrastra: un clic elige el
   * bloque entero, y la hora de fin la pone la configuración, no la mano.
   */
  filasFijasPorEspacio?: (espacioId: string) => number | undefined;
  onElegido: (hueco: { espacioId: string; filaInicio: number; filaFin: number }) => void;
}) => {
  const [seleccion, setSeleccion] = useState<Seleccion | null>(null);
  const [arrastrando, setArrastrando] = useState(false);

  const empezar = useCallback(
    (espacioId: string, fila: number) => {
      if (!activo) return;
      if (ocupadasPorEspacio(espacioId).has(fila)) return;
      const fijas = filasFijasPorEspacio?.(espacioId);
      const hasta = fijas ? Math.min(fila + fijas - 1, totalFilas - 1) : fila;
      setSeleccion({ espacioId, ancla: fila, desde: fila, hasta });
      setArrastrando(true);
    },
    [activo, ocupadasPorEspacio, filasFijasPorEspacio, totalFilas]
  );

  const extender = useCallback(
    (espacioId: string, fila: number) => {
      if (!arrastrando) return;
      // Duración fija: el bloque ya quedó elegido al pulsar.
      if (filasFijasPorEspacio?.(espacioId)) return;
      setSeleccion((previa) => {
        // El arrastre no cambia de sala a media selección: sería confuso y no
        // significa nada reservar "de una sala a otra".
        if (!previa || previa.espacioId !== espacioId) return previa;

        const ocupadas = ocupadasPorEspacio(espacioId);
        const { ancla } = previa;
        let desde = Math.min(ancla, fila);
        let hasta = Math.max(ancla, fila);

        // Se recorta antes de lo ocupado, hacia el lado al que se arrastra.
        if (fila >= ancla) {
          for (let f = ancla; f <= hasta; f++) {
            if (ocupadas.has(f)) { hasta = f - 1; break; }
          }
          desde = ancla;
        } else {
          for (let f = ancla; f >= desde; f--) {
            if (ocupadas.has(f)) { desde = f + 1; break; }
          }
          hasta = ancla;
        }

        if (hasta < desde) return previa;
        return { ...previa, desde, hasta };
      });
    },
    [arrastrando, ocupadasPorEspacio, filasFijasPorEspacio]
  );

  const soltar = useCallback(() => {
    if (!arrastrando) return;
    setArrastrando(false);
    if (seleccion) {
      onElegido({
        espacioId: seleccion.espacioId,
        filaInicio: seleccion.desde,
        filaFin: Math.min(seleccion.hasta, totalFilas - 1),
      });
    }
  }, [arrastrando, seleccion, onElegido, totalFilas]);

  const limpiar = useCallback(() => {
    setSeleccion(null);
    setArrastrando(false);
  }, []);

  // Soltar fuera de la rejilla también cuenta, y Escape cancela sin más.
  useEffect(() => {
    if (!arrastrando) return undefined;
    const alSoltar = () => soltar();
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') limpiar();
    };
    window.addEventListener('mouseup', alSoltar);
    window.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('mouseup', alSoltar);
      window.removeEventListener('keydown', alTeclear);
    };
  }, [arrastrando, soltar, limpiar]);

  return { seleccion, arrastrando, empezar, extender, soltar, limpiar };
};
